import { HttpMethod, RawHttpResponse } from './http';

export class ApiBaseException<TBody = unknown> extends Error {
  readonly url: string;
  readonly method: HttpMethod;
  readonly status: number;
  readonly body: TBody | null;

  constructor(
    url: string,
    method: HttpMethod,
    status: number,
    message: string,
    body?: TBody | null,
  ) {
    super(message);
    this.name = 'ApiBaseException';
    this.url = url;
    this.method = method;
    this.status = status;
    this.body = body ?? null;
  }

  static fromResponse<TBody = any>(
    url: string,
    method: HttpMethod,
    response: RawHttpResponse<TBody>,
  ) {
    const { status, data } = response;

    const prefix = `${method.toUpperCase()} ${url} ${status}`;
    let message = `${prefix} failed with status ${status}`;

    if (data && typeof data === 'object') {
      const anyBody = data as any;

      // Typical NestJS error: { statusCode, message, error }
      if (typeof anyBody.message === 'string') {
        message = `${prefix} ${anyBody.message}`;
      } else if (Array.isArray(anyBody.message)) {
        message = `${prefix} ${anyBody.message.join('; ')}`;
      } else if (typeof anyBody.error === 'string') {
        message = `${prefix} ${anyBody.error}`;
      }
    }

    return new ApiBaseException<TBody>(url, method, status, message, data);
  }
}

export function ensureSuccess<TBody>(
  url: string,
  method: HttpMethod,
  response: RawHttpResponse<TBody>,
): RawHttpResponse<TBody> {
  if (response.status < 200 || response.status >= 300) {
    throw ApiBaseException.fromResponse(url, method, response);
  }
  return response;
}
